/**
 * AppGuidePage — `/app-guide`
 *
 * The single-page App Guide that replaces TradingGuide / TradingViewSetup /
 * OptionsGuide / HelpHub. Three-pane layout (TOC + content + on-page rail)
 * with top-bar fuzzy search and URL-hash-driven section/anchor routing.
 *
 * The sidebar nav uses `target="_blank"` so the page lives in its own
 * browser tab — readers keep the live app open in the other tab and the
 * <OpenInApp> chips inside the guide focus/navigate that other tab.
 *
 * URL shape: `/app-guide#<section-id>[/<anchor>]`. Example:
 *   /app-guide#charts/order-flow-strip
 * The section before the slash matches a registered section's id; the
 * portion after the slash (optional) names a sub-anchor inside that
 * section's content.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, AppBar, Toolbar, Drawer, IconButton } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { SECTIONS, SECTION_BY_ID } from '../components/app-guide/sections';
import GuideSidebar from '../components/app-guide/GuideSidebar';
import GuideContent from '../components/app-guide/GuideContent';
import GuideOnPage from '../components/app-guide/GuideOnPage';
import GuideSearch from '../components/app-guide/GuideSearch';
import { useAppTheme } from '../hooks/useAppTheme';

const DEFAULT_SECTION = 'quick-start';

/** Parse `#section[/anchor]` from a hash like `#charts/order-flow-strip`. */
function parseHash(hash: string): { section: string; anchor?: string } {
  const raw = (hash || '').replace(/^#/, '');
  if (!raw) return { section: DEFAULT_SECTION };
  const [section, anchor] = raw.split('/');
  return { section: section || DEFAULT_SECTION, anchor: anchor || undefined };
}

function writeHash(section: string, anchor?: string) {
  const next = anchor ? `#${section}/${anchor}` : `#${section}`;
  if (window.location.hash !== next) {
    // Use pushState so back-button walks through previous sections.
    window.history.pushState(null, '', next);
  }
}

const AppGuidePage: React.FC = () => {
  const { C, isDark } = useAppTheme();
  const [{ section, anchor }, setRoute] = useState(parseHash(window.location.hash));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sync with browser hash changes (back/forward, manual edit).
  useEffect(() => {
    const onHashChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const active = useMemo(() => SECTION_BY_ID[section] || SECTIONS[0], [section]);

  const onSelect = (id: string) => {
    writeHash(id);
    setRoute({ section: id, anchor: undefined });
    setMobileOpen(false);
  };
  const onAnchor = (a: string) => {
    writeHash(active.id, a);
    setRoute({ section: active.id, anchor: a });
  };
  const onSearchPick = (id: string, a?: string) => {
    writeHash(id, a);
    setRoute({ section: id, anchor: a });
  };

  return (
    <Box
      className="app-guide-root"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
        // R81 Phase J — print stylesheet. Sidebar + on-page rail + search
        // are hidden in print so the content paginates cleanly into a PDF.
        '@media print': {
          '& .app-guide-sidebar, & .app-guide-onpage, & .app-guide-topbar': { display: 'none !important' },
          '& .app-guide-content': { maxWidth: 'none', padding: 0 },
          backgroundColor: '#ffffff',
          color: '#000',
        },
      }}
    >
      {/* Top bar with brand + search */}
      <AppBar
        position="sticky"
        elevation={0}
        className="app-guide-topbar"
        sx={{
          bgcolor: isDark ? 'background.paper' : '#ffffff',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 1.5, minHeight: 56 }}>
          <IconButton sx={{ display: { md: 'none' } }} onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </IconButton>
          <MenuBookIcon sx={{ color: C.accent }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.1 }}>
              earngush App Guide
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.1 }}>
              Read while you navigate the app in your other tab
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <GuideSearch onPick={onSearchPick} />
        </Toolbar>
      </AppBar>

      {/* Body — sidebar + content + on-page */}
      <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
        {/* Desktop sidebar */}
        <Box className="app-guide-sidebar" sx={{ display: { xs: 'none', md: 'block' } }}>
          <GuideSidebar activeId={active.id} onSelect={onSelect} />
        </Box>
        {/* Mobile drawer */}
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
          <Box sx={{ width: 280, display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
            <IconButton onClick={() => setMobileOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <GuideSidebar activeId={active.id} onSelect={onSelect} />
        </Drawer>

        <GuideContent section={active} anchor={anchor} />
        <GuideOnPage section={active} onAnchor={onAnchor} />
      </Box>
    </Box>
  );
};

export default AppGuidePage;
